import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Plus, Trash2, Edit2, X, Check, Euro, Calendar, Clock, Users, Settings } from 'lucide-react';
import { tarifsAPI, settingsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminTarifs = () => {
  const [tarifs, setTarifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [fraisInscription, setFraisInscription] = useState(25);
  const [savingFrais, setSavingFrais] = useState(false);
  const [vatRate, setVatRate] = useState(20);
  const [pricesAreTtc, setPricesAreTtc] = useState(true);
  const [newTarif, setNewTarif] = useState({
    groupe: '',
    age_range: '',
    niveau: '',
    jour: '',
    horaire: '',
    prix_1er: 0,
    prix_2e: 0,
    prix_3e: 0,
    is_cpf: false,
    cpf_url: '',
    order: 0
  });
  const { toast } = useToast();

  useEffect(() => {
    loadTarifs();
    loadSettings();
  }, []);

  const loadTarifs = async () => {
    try {
      const res = await tarifsAPI.list();
      setTarifs(res.data);
    } catch (error) {
      console.error('Error loading tarifs:', error);
      toast({ title: 'Erreur', description: 'Impossible de charger les tarifs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const res = await settingsAPI.get();
      setFraisInscription(res.data.frais_inscription || 25);
      setVatRate(res.data.vat_rate ?? 20);
      setPricesAreTtc(res.data.prices_are_ttc !== false);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  // Compute HT/TTC/TVA breakdown for a given amount
  const breakdownPrice = (amount) => {
    const value = parseFloat(amount) || 0;
    if (!value) return { ht: 0, tva: 0, ttc: 0 };
    if (pricesAreTtc) {
      const ttc = value;
      const ht = vatRate ? ttc / (1 + vatRate / 100) : ttc;
      return { ht: ht.toFixed(2), tva: (ttc - ht).toFixed(2), ttc: ttc.toFixed(2) };
    }
    const ht = value;
    const ttc = ht * (1 + vatRate / 100);
    return { ht: ht.toFixed(2), tva: (ttc - ht).toFixed(2), ttc: ttc.toFixed(2) };
  };

  const saveFraisInscription = async () => {
    setSavingFrais(true);
    try {
      await settingsAPI.update({ frais_inscription: fraisInscription });
      toast({ title: 'Succès', description: 'Frais d\'inscription mis à jour' });
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    } finally {
      setSavingFrais(false);
    }
  };

  const handleEdit = (tarif) => {
    setEditingId(tarif.id);
    setEditForm({ ...tarif });
  };

  const handleSave = async () => {
    try {
      await tarifsAPI.update(editingId, editForm);
      toast({ title: 'Succès', description: 'Tarif mis à jour' });
      setEditingId(null);
      loadTarifs();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce tarif ?')) return;
    try {
      await tarifsAPI.delete(id);
      toast({ title: 'Succès', description: 'Tarif supprimé' });
      loadTarifs();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de supprimer', variant: 'destructive' });
    }
  };

  const handleCreate = async () => {
    try {
      await tarifsAPI.create(newTarif);
      toast({ title: 'Succès', description: 'Tarif créé' });
      setShowAddForm(false);
      setNewTarif({
        groupe: '',
        age_range: '',
        niveau: '',
        jour: '',
        horaire: '',
        prix_1er: 0,
        prix_2e: 0,
        prix_3e: 0,
        is_cpf: false,
        cpf_url: '',
        order: tarifs.length + 1
      });
      loadTarifs();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de créer le tarif', variant: 'destructive' });
    }
  };

  const jours = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI'];

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-tarifs">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Gestion des Tarifs</h1>
            <p className="text-sm text-[#6777A1] mt-1">Modifiez les prix des formations</p>
          </div>
          <Button
            onClick={() => setShowAddForm(true)}
            className="bg-[#285BBB] hover:bg-[#2C3C65]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Ajouter un tarif
          </Button>
        </div>

        {/* Frais d'inscription */}
        <div className="bg-gradient-to-r from-[#285BBB] to-[#2C3C65] rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Settings className="h-6 w-6" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Frais d&apos;inscription annuels</h3>
                <p className="text-blue-100 text-sm">Appliqués à chaque nouvelle inscription</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center bg-white/20 rounded-lg px-3">
                <Input
                  type="number"
                  value={fraisInscription}
                  onChange={(e) => setFraisInscription(parseFloat(e.target.value) || 0)}
                  className="w-20 bg-transparent border-0 text-white text-xl font-bold text-center focus:ring-0"
                />
                <span className="text-xl font-bold">€</span>
              </div>
              <Button
                onClick={saveFraisInscription}
                disabled={savingFrais}
                variant="secondary"
                className="bg-white text-[#285BBB] hover:bg-blue-50"
              >
                {savingFrais ? 'Sauvegarde...' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        </div>

        {/* Mode HT/TTC banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="vat-banner">
          <div className="flex items-center gap-3 text-sm">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${pricesAreTtc ? 'bg-[#285BBB] text-white' : 'bg-amber-600 text-white'}`}>
              Prix saisis {pricesAreTtc ? 'TTC' : 'HT'}
            </span>
            <span className="text-amber-900">
              TVA configurée : <strong>{vatRate}%</strong>
            </span>
          </div>
          <a
            href="/admin/settings"
            className="text-xs text-[#285BBB] hover:underline font-medium"
          >
            Modifier dans Paramètres →
          </a>
        </div>

        {/* Add Form */}
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-[#2C3C65]">Nouveau Tarif</h2>
              <button onClick={() => setShowAddForm(false)}>
                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Groupe/Cours</label>
                <Input
                  value={newTarif.groupe}
                  onChange={(e) => setNewTarif({ ...newTarif, groupe: e.target.value })}
                  placeholder="Cambridge Starters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Tranche d&apos;âge</label>
                <Input
                  value={newTarif.age_range}
                  onChange={(e) => setNewTarif({ ...newTarif, age_range: e.target.value })}
                  placeholder="6-8 ans"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Niveau</label>
                <Input
                  value={newTarif.niveau}
                  onChange={(e) => setNewTarif({ ...newTarif, niveau: e.target.value })}
                  placeholder="Pre-A1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Jour</label>
                <select
                  value={newTarif.jour}
                  onChange={(e) => setNewTarif({ ...newTarif, jour: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">Sélectionner</option>
                  {jours.map(j => <option key={j} value={j}>{j}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">Horaire</label>
                <Input
                  value={newTarif.horaire}
                  onChange={(e) => setNewTarif({ ...newTarif, horaire: e.target.value })}
                  placeholder="09H00-10H30"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                  Prix 1er enfant ({pricesAreTtc ? 'TTC' : 'HT'})
                </label>
                <Input
                  type="number"
                  value={newTarif.prix_1er}
                  onChange={(e) => setNewTarif({ ...newTarif, prix_1er: parseFloat(e.target.value) || 0 })}
                />
                {!!newTarif.prix_1er && (
                  <p className="text-xs text-[#6777A1] mt-1">
                    {pricesAreTtc ? `HT: ${breakdownPrice(newTarif.prix_1er).ht}€` : `TTC: ${breakdownPrice(newTarif.prix_1er).ttc}€`}
                    {' · '}TVA: {breakdownPrice(newTarif.prix_1er).tva}€
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                  Prix 2ème enfant ({pricesAreTtc ? 'TTC' : 'HT'})
                </label>
                <Input
                  type="number"
                  value={newTarif.prix_2e}
                  onChange={(e) => setNewTarif({ ...newTarif, prix_2e: parseFloat(e.target.value) || 0 })}
                />
                {!!newTarif.prix_2e && (
                  <p className="text-xs text-[#6777A1] mt-1">
                    {pricesAreTtc ? `HT: ${breakdownPrice(newTarif.prix_2e).ht}€` : `TTC: ${breakdownPrice(newTarif.prix_2e).ttc}€`}
                    {' · '}TVA: {breakdownPrice(newTarif.prix_2e).tva}€
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-1">
                  Prix 3ème enfant ({pricesAreTtc ? 'TTC' : 'HT'})
                </label>
                <Input
                  type="number"
                  value={newTarif.prix_3e}
                  onChange={(e) => setNewTarif({ ...newTarif, prix_3e: parseFloat(e.target.value) || 0 })}
                />
                {!!newTarif.prix_3e && (
                  <p className="text-xs text-[#6777A1] mt-1">
                    {pricesAreTtc ? `HT: ${breakdownPrice(newTarif.prix_3e).ht}€` : `TTC: ${breakdownPrice(newTarif.prix_3e).ttc}€`}
                    {' · '}TVA: {breakdownPrice(newTarif.prix_3e).tva}€
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newTarif.is_cpf}
                  onChange={(e) => setNewTarif({ ...newTarif, is_cpf: e.target.checked })}
                />
                <span className="text-sm text-[#2C3C65]">Éligible CPF</span>
              </label>
              {newTarif.is_cpf && (
                <Input
                  value={newTarif.cpf_url}
                  onChange={(e) => setNewTarif({ ...newTarif, cpf_url: e.target.value })}
                  placeholder="URL Mon Compte Formation"
                  className="flex-1"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowAddForm(false)}>Annuler</Button>
              <Button onClick={handleCreate} className="bg-[#285BBB] hover:bg-[#2C3C65]">
                <Check className="h-4 w-4 mr-2" />
                Créer
              </Button>
            </div>
          </motion.div>
        )}

        {/* Tarifs Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#2C3C65] text-white">
                  <tr>
                    <th className="py-3 px-4 text-left text-sm font-medium">Groupe/Cours</th>
                    <th className="py-3 px-4 text-left text-sm font-medium">Âge</th>
                    <th className="py-3 px-4 text-left text-sm font-medium">Niveau</th>
                    <th className="py-3 px-4 text-left text-sm font-medium">Jour</th>
                    <th className="py-3 px-4 text-left text-sm font-medium">Horaire</th>
                    <th className="py-3 px-4 text-center text-sm font-medium">1er</th>
                    <th className="py-3 px-4 text-center text-sm font-medium">2ème</th>
                    <th className="py-3 px-4 text-center text-sm font-medium">3ème</th>
                    <th className="py-3 px-4 text-center text-sm font-medium">CPF</th>
                    <th className="py-3 px-4 text-center text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {tarifs.map((tarif) => (
                    <tr key={tarif.id} className="hover:bg-gray-50">
                      {editingId === tarif.id ? (
                        <>
                          <td className="py-2 px-2">
                            <Input
                              value={editForm.groupe}
                              onChange={(e) => setEditForm({ ...editForm, groupe: e.target.value })}
                              className="text-sm"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              value={editForm.age_range}
                              onChange={(e) => setEditForm({ ...editForm, age_range: e.target.value })}
                              className="text-sm w-24"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              value={editForm.niveau}
                              onChange={(e) => setEditForm({ ...editForm, niveau: e.target.value })}
                              className="text-sm w-20"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <select
                              value={editForm.jour}
                              onChange={(e) => setEditForm({ ...editForm, jour: e.target.value })}
                              className="border rounded px-2 py-1 text-sm"
                            >
                              {jours.map(j => <option key={j} value={j}>{j}</option>)}
                            </select>
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              value={editForm.horaire}
                              onChange={(e) => setEditForm({ ...editForm, horaire: e.target.value })}
                              className="text-sm w-28"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              value={editForm.prix_1er}
                              onChange={(e) => setEditForm({ ...editForm, prix_1er: parseFloat(e.target.value) || 0 })}
                              className="text-sm w-20 text-center"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              value={editForm.prix_2e}
                              onChange={(e) => setEditForm({ ...editForm, prix_2e: parseFloat(e.target.value) || 0 })}
                              className="text-sm w-20 text-center"
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              value={editForm.prix_3e}
                              onChange={(e) => setEditForm({ ...editForm, prix_3e: parseFloat(e.target.value) || 0 })}
                              className="text-sm w-20 text-center"
                            />
                          </td>
                          <td className="py-2 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={editForm.is_cpf}
                              onChange={(e) => setEditForm({ ...editForm, is_cpf: e.target.checked })}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={handleSave}
                                className="p-1 text-green-600 hover:bg-green-50 rounded"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setEditingId(null)}
                                className="p-1 text-gray-500 hover:bg-gray-100 rounded"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-3 px-4 font-medium text-[#2C3C65]">{tarif.groupe}</td>
                          <td className="py-3 px-4 text-sm text-[#6777A1]">{tarif.age_range}</td>
                          <td className="py-3 px-4 text-sm">
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                              {tarif.niveau}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-[#6777A1]">{tarif.jour}</td>
                          <td className="py-3 px-4 text-sm text-[#6777A1]">{tarif.horaire}</td>
                          <td className="py-3 px-4 text-center font-semibold text-[#285BBB]">
                            <div>{tarif.prix_1er}€</div>
                            {!!tarif.prix_1er && (
                              <div className="text-[10px] font-normal text-[#6777A1] leading-tight">
                                <div>{pricesAreTtc ? `HT ${breakdownPrice(tarif.prix_1er).ht}€` : `TTC ${breakdownPrice(tarif.prix_1er).ttc}€`}</div>
                                <div className="text-amber-700">TVA {breakdownPrice(tarif.prix_1er).tva}€</div>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center font-semibold text-[#285BBB]">
                            <div>{tarif.prix_2e}€</div>
                            {!!tarif.prix_2e && (
                              <div className="text-[10px] font-normal text-[#6777A1] leading-tight">
                                <div>{pricesAreTtc ? `HT ${breakdownPrice(tarif.prix_2e).ht}€` : `TTC ${breakdownPrice(tarif.prix_2e).ttc}€`}</div>
                                <div className="text-amber-700">TVA {breakdownPrice(tarif.prix_2e).tva}€</div>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center font-semibold text-[#285BBB]">
                            <div>{tarif.prix_3e}€</div>
                            {!!tarif.prix_3e && (
                              <div className="text-[10px] font-normal text-[#6777A1] leading-tight">
                                <div>{pricesAreTtc ? `HT ${breakdownPrice(tarif.prix_3e).ht}€` : `TTC ${breakdownPrice(tarif.prix_3e).ttc}€`}</div>
                                <div className="text-amber-700">TVA {breakdownPrice(tarif.prix_3e).tva}€</div>
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {tarif.is_cpf ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs">Oui</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">Non</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center gap-1">
                              <button
                                onClick={() => handleEdit(tarif)}
                                className="p-1.5 text-[#285BBB] hover:bg-blue-50 rounded"
                                title="Modifier"
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(tarif.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                                title="Supprimer"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Euro className="h-5 w-5 text-[#285BBB] mt-0.5" />
            <div>
              <h3 className="font-semibold text-[#2C3C65]">À propos des tarifs</h3>
              <p className="text-sm text-[#6777A1] mt-1">
                Les tarifs dégressifs s&apos;appliquent automatiquement lors de l&apos;inscription de plusieurs enfants d&apos;une même famille.
                Les cours éligibles CPF sont marqués et peuvent être financés via Mon Compte Formation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminTarifs;
